import './App.css';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import Footer from './components/Footer';
import Header from './components/Header/Header';
import Home from './components/Home';
import About from './components/About';
import Impact from './components/Impact';
import Projects from './components/Projects';
import Contact from './components/Contact';
import Donate from './components/Donate';
import Events from './components/Events';
import EventDetail from './components/EventDetail';
import Resources from './components/Resources';
import Login from './components/Login';
import Register from './components/Register';
import Profile from './components/Profile';
import MyRegistrations from './components/MyRegistrations';
import MyCertificates from './components/MyCertificates';
import AdminDashboard from './components/AdminDashboard';
import NotFound from './components/NotFound';
import TranslationLoading from './components/TranslationLoading';
import { AuthProvider } from './utils/AuthContext';
import { ProtectedRoute, AdminRoute, PublicOnlyRoute } from './utils/ProtectedRoutes';

// Layout wrapper component to conditionally adjust layout for admin routes
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div className="App flex flex-col min-h-screen">
      {/* Header component has internal logic to hide itself on admin routes */}
      <Header />
      
      <main className={`flex-grow ${!isAdminRoute ? 'bg-gray-100 mt-2' : ''}`}>
        {children}
      </main>
      
      {/* Don't show footer on admin routes */}
      {!isAdminRoute && <Footer />}
    </div>
  );
};

function App() {
  const { i18n } = useTranslation();
  const [isTranslationLoading, setIsTranslationLoading] = useState(false);
  
  // Add a global translation loading indicator
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      if (lng !== 'en') {
        setIsTranslationLoading(true);
        // Hide loading after a timeout to allow translations to complete
        setTimeout(() => setIsTranslationLoading(false), 1000);
      } else {
        setIsTranslationLoading(false);
      }
    };
    
    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);
  
  return (
    <AuthProvider>
      <Router>
        <AppLayout>
          {/* Translation loading indicator */}
          {isTranslationLoading && (
            <div className="fixed top-20 right-4 z-50">
              <TranslationLoading />
            </div>
          )}
          
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/impact" element={<Impact />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:id" element={<EventDetail />} />
            <Route path="/resources" element={<Resources />} />
            
            {/* Authentication Routes (accessible only when not logged in) */}
            <Route 
              path="/login" 
              element={
                <PublicOnlyRoute>
                  <Login />
                </PublicOnlyRoute>
              } 
            />
            
            <Route 
              path="/register" 
              element={
                <PublicOnlyRoute>
                  <Register />
                </PublicOnlyRoute>
              } 
            />
            
            {/* Protected User Routes */}
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            
            <Route
              path="/my-registrations"
              element={
                <ProtectedRoute>
                  <MyRegistrations />
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-certificates"
              element={
                <ProtectedRoute>
                  <MyCertificates />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Admin Routes - No Header/Footer here */}
            <Route 
              path="/admin/*" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            
            {/* Handle /admin/dashboard explicitly without creating a new AdminDashboard instance */}
            <Route 
              path="/admin/dashboard/*" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppLayout>
      </Router>
    </AuthProvider>
  );
}

export default App;
